import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Shield, RefreshCw, Filter, AlertTriangle, ExternalLink } from "lucide-react";
import { api } from "../lib/apiClient";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";

const PATTERN_LABELS = {
  ignore_previous: "Ignorar instrucciones previas",
  role_swap_you_are_now: "Cambio de rol",
  system_role_inject: "Inyección de rol system",
  reveal_system_prompt: "Revelar prompt de sistema",
  output_format_hijack: "Hijack de formato de salida",
  jailbreak_dan: "Jailbreak DAN / modo desarrollador",
  override_safety: "Bypass de filtros de seguridad",
  instruction_marker: "Marcador de instrucción",
  data_exfiltration: "Exfiltración de datos",
  prompt_terminator: "Terminador de prompt",
};

const SOURCE_PREFIXES = [
  { value: "", label: "Todas" },
  { value: "n0.", label: "Prevalidación N0" },
  { value: "n1.", label: "Diagnóstico N1" },
  { value: "n2.", label: "Kit Documental N2" },
  { value: "composer.", label: "Composer Emails" },
];

export default function AdminPromptInjectionPage() {
  const [data, setData] = useState({ items: [], count: 0, by_pattern: [], by_source: [] });
  const [loading, setLoading] = useState(true);
  const [sourcePrefix, setSourcePrefix] = useState("");
  const [patternFilter, setPatternFilter] = useState("");
  const [reloadTick, setReloadTick] = useState(0);

  useEffect(() => {
    let alive = true;
    Promise.resolve().then(() => { if (alive) setLoading(true); });
    const url = `/admin/security/prompt-injection-attempts?limit=300${sourcePrefix ? `&source_prefix=${encodeURIComponent(sourcePrefix)}` : ""}`;
    api.get(url)
      .then(({ data: res }) => { if (alive) setData(res); })
      .catch(() => {})
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [sourcePrefix, reloadTick]);

  const filteredItems = useMemo(() => {
    if (!patternFilter) return data.items;
    return data.items.filter((it) => (it.patterns || []).includes(patternFilter));
  }, [data.items, patternFilter]);

  const refresh = useCallback(() => setReloadTick((t) => t + 1), []);

  return (
    <div data-testid="admin-prompt-injection-page" className="space-y-6">
      <header className="flex items-center justify-between border-b border-gray-200 pb-3">
        <div>
          <h1 className="font-serif text-2xl text-gray-900">Intentos de Prompt-Injection</h1>
          <p className="text-xs text-gray-500 mt-1">
            Eventos detectados y neutralizados automáticamente por la defensa multicapa.
          </p>
        </div>
        <Button
          variant="outline"
          onClick={refresh}
          disabled={loading}
          className="rounded-sm h-9 text-xs"
          data-testid="prompt-injection-refresh"
        >
          <RefreshCw size={14} className={`mr-1.5 ${loading ? "animate-spin" : ""}`} /> Actualizar
        </Button>
      </header>

      {/* Resumen */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <KpiCard
          label="Total detectado"
          value={data.count}
          icon={<AlertTriangle size={16} className="text-amber-600" />}
          testid="kpi-total"
        />
        <KpiCard
          label="Patrones distintos"
          value={(data.by_pattern || []).length}
          icon={<Shield size={16} className="text-emerald-600" />}
          testid="kpi-patterns"
        />
        <KpiCard
          label="Sources únicos"
          value={(data.by_source || []).length}
          icon={<Filter size={16} className="text-indigo-600" />}
          testid="kpi-sources"
        />
      </div>

      {/* Filtros */}
      <div className="border border-gray-200 bg-white p-4" data-testid="prompt-injection-filters">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-gray-500 mb-1">
              Source
            </label>
            <select
              value={sourcePrefix}
              onChange={(e) => setSourcePrefix(e.target.value)}
              className="w-full h-9 px-2 text-xs border border-gray-300 rounded-sm bg-white"
              data-testid="filter-source"
            >
              {SOURCE_PREFIXES.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-[10px] uppercase tracking-widest text-gray-500 mb-1">
              Patrón
            </label>
            <select
              value={patternFilter}
              onChange={(e) => setPatternFilter(e.target.value)}
              className="w-full h-9 px-2 text-xs border border-gray-300 rounded-sm bg-white"
              data-testid="filter-pattern"
            >
              <option value="">Todos</option>
              {(data.by_pattern || []).map((p) => (
                <option key={p.pattern} value={p.pattern}>
                  {PATTERN_LABELS[p.pattern] || p.pattern} ({p.count})
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Tabla de eventos */}
      <div className="border border-gray-200 bg-white overflow-hidden" data-testid="prompt-injection-table-wrap">
        <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
          <h2 className="font-serif text-base">Eventos ({filteredItems.length})</h2>
          {loading && <span className="text-xs text-gray-500">Cargando…</span>}
        </div>
        {filteredItems.length === 0 ? (
          <div className="p-8 text-center text-sm text-gray-500" data-testid="prompt-injection-empty">
            Sin intentos detectados en este filtro. La defensa anti-injection está activa.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="prompt-injection-table">
              <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-gray-600">
                <tr>
                  <th className="px-3 py-2 text-left">Cuándo</th>
                  <th className="px-3 py-2 text-left">Source</th>
                  <th className="px-3 py-2 text-left">Patrones</th>
                  <th className="px-3 py-2 text-left">Usuario</th>
                  <th className="px-3 py-2 text-left">Caso</th>
                  <th className="px-3 py-2 text-left">Snippet</th>
                </tr>
              </thead>
              <tbody>
                {filteredItems.map((it, i) => (
                  <tr key={i} className="border-t border-gray-100 hover:bg-gray-50" data-testid={`prompt-injection-row-${i}`}>
                    <td className="px-3 py-2 text-gray-600 whitespace-nowrap">
                      {it.ts ? new Date(it.ts).toLocaleString("es-CO") : "—"}
                    </td>
                    <td className="px-3 py-2 font-mono text-[11px] text-gray-700">{it.source || "—"}</td>
                    <td className="px-3 py-2">
                      <div className="flex flex-wrap gap-1">
                        {(it.patterns || []).map((p) => (
                          <span
                            key={p}
                            className="px-1.5 py-0.5 bg-amber-50 border border-amber-200 text-amber-800 rounded-sm text-[10px]"
                            title={PATTERN_LABELS[p] || p}
                          >
                            {p}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-gray-700">{it.user_id || "—"}</td>
                    <td className="px-3 py-2 font-mono text-[11px] text-gray-700">
                      {it.case_id ? (
                        <a
                          href={`/admin/cases/${it.case_id}`}
                          className="hover:underline inline-flex items-center gap-0.5"
                          data-testid={`prompt-injection-case-link-${i}`}
                        >
                          {it.case_id.slice(0, 8)} <ExternalLink size={10} />
                        </a>
                      ) : "—"}
                    </td>
                    <td className="px-3 py-2 text-gray-700 max-w-md truncate" title={it.snippet}>
                      {it.snippet || "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function KpiCard({ label, value, icon, testid }) {
  return (
    <div className="border border-gray-200 bg-white p-4" data-testid={testid}>
      <div className="flex items-center justify-between text-[10px] uppercase tracking-widest text-gray-500 mb-1">
        <span>{label}</span>
        {icon}
      </div>
      <div className="font-serif text-2xl text-gray-900">{value}</div>
    </div>
  );
}
